import React, { Component, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import Lenis from "lenis";
import { LanguageProvider, langFromPath } from "@/lib/i18n";
import { AuthProvider } from "@/lib/auth";
import Home from "@/pages/Home";
import Apply from "@/pages/Apply";
import Expeditions from "@/pages/Expeditions";
import ExpeditionDetail from "@/pages/ExpeditionDetail";
import Leaders from "@/pages/Leaders";
import LeaderDetail from "@/pages/LeaderDetail";
import Contact from "@/pages/Contact";
import NotFound from "@/pages/NotFound";
import Destinations from "@/pages/Destinations";
import DestinationDetail from "@/pages/DestinationDetail";
import About from "@/pages/About";
import Faq from "@/pages/Faq";
import Privacy from "@/pages/Privacy";
import CookiePolicy from "@/pages/CookiePolicy";
import CookieBanner from "@/components/site/CookieBanner";
import Login from "@/admin/Login";
import AuthCallback from "@/admin/AuthCallback";
import ForgotPassword from "@/admin/ForgotPassword";
import ResetPassword from "@/admin/ResetPassword";
import AdminLayout, { AdminOnly } from "@/admin/AdminLayout";
import Dashboard from "@/admin/Dashboard";
import AdminTours from "@/admin/AdminTours";
import AdminLeaders from "@/admin/AdminLeaders";
import AdminMedia from "@/admin/AdminMedia";
import AdminHome from "@/admin/AdminHome";
import AdminPages from "@/admin/AdminPages";
import AdminCRM from "@/admin/AdminCRM";
import AdminPlanning from "@/admin/AdminPlanning";

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  render() {
    if (this.state.error) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-obsidian text-cream p-8 text-center">
          <div>
            <p className="font-serif text-3xl mb-3">Qualcosa si è perso nella selva.</p>
            <p className="text-cream2 text-sm">{String(this.state.error)}</p>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function SmoothScroll() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (pathname.startsWith("/admin")) return undefined;
    const lenis = new Lenis({ lerp: 0.22, wheelMultiplier: 1.35, smoothWheel: true });
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, [pathname]);
  return null;
}

function RouterShell() {
  const location = useLocation();
  // Google OAuth (Emergent gestito): processa il session_id PRIMA di ogni check di sessione
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <>
      <ScrollToTop />
      <SmoothScroll />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/viaggi" element={<Expeditions />} />
        <Route path="/viaggi/:slug" element={<ExpeditionDetail />} />
        <Route path="/destinazioni" element={<Destinations />} />
        <Route path="/destinazioni/:slug" element={<DestinationDetail />} />
        <Route path="/leader" element={<Leaders />} />
        <Route path="/leader/:slug" element={<LeaderDetail />} />
        <Route path="/chi-siamo" element={<About />} />
        <Route path="/faq" element={<Faq />} />
        <Route path="/contatti" element={<Contact />} />
        <Route path="/candidatura" element={<Apply />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/cookie-policy" element={<CookiePolicy />} />
        <Route path="/admin/login" element={<Login />} />
        <Route path="/admin/password-dimenticata" element={<ForgotPassword />} />
        <Route path="/admin/reset-password" element={<ResetPassword />} />
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<Dashboard />} />
          <Route path="viaggi" element={<AdminTours />} />
          <Route path="home" element={<AdminOnly><AdminHome /></AdminOnly>} />
          <Route path="pagine" element={<AdminOnly><AdminPages /></AdminOnly>} />
          <Route path="leader" element={<AdminLeaders />} />
          <Route path="media" element={<AdminOnly><AdminMedia /></AdminOnly>} />
          <Route path="crm" element={<AdminCRM />} />
          <Route path="planning" element={<AdminOnly><AdminPlanning /></AdminOnly>} />
        </Route>
        <Route path="*" element={<NotFound />} />
      </Routes>
    </>
  );
}

export default function App() {
  const basename = langFromPath(window.location.pathname);
  return (
    <ErrorBoundary>
      <LanguageProvider>
        <AuthProvider>
          <BrowserRouter basename={basename === "it" ? "/" : `/${basename}`}>
            <RouterShell />
            <CookieBanner />
          </BrowserRouter>
          <Toaster theme="dark" position="top-right" />
        </AuthProvider>
      </LanguageProvider>
    </ErrorBoundary>
  );
}
